import React, { useEffect, useState } from "react";
import T from "prop-types";
import { flatten, get, uniq } from "lodash";
import { Map } from "immutable";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import { toastr } from "react-redux-toastr";
import { Authorized, Button, Search } from "MODULES/shared/components";
import {
  FilterSelect,
  Pagination,
  SearchFilter,
  SearchableFilterSelect
} from "MODULES/shared/components/Pagination";
import {
  ADMIN,
  CAN_ADD_UPDATE_PARTS,
  EXPORT_LIMIT,
  ROLE,
  USER
} from "MODULES/users/constants";
import { matchesBoolean } from "LIB/filters";
import { SUCCESS } from "LIB/submission";
import { stringify } from "query-string";
import { renderIf } from "MODULES/shared/decorators";
import useDebounce from "APP_ROOT/hooks/useDebounce";
import api from "APP_ROOT/api";
import TitleWithFilters from "MODULES/parts/components/Search/TitleWithFilters";
import { API_BASE } from "APP_ROOT/constants";
import { hasPermission } from "APP_ROOT/modules/users/helpers";
import Table from "./Table";
import NewPart from "./NewPart";
import { partsPagination } from "../../pagination";

const defaultFilters = [
  {
    label: "No Sellable Qty",
    filters: {
      sellable_qty: false
    }
  }
];

const filterTags = [
  {
    label: "Active Parts",
    key: "active"
  },
  {
    label: "Sellable Qty",
    key: "sellable_qty",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "No Sellable Qty",
    key: "sellable_qty",
    matchesTarget: matchesBoolean(false)
  }
];

const ExportButton = ({ searchProps }) => {
  const params = searchProps.paginator.get("filters").toJS();
  const partsTotalCount = searchProps.paginator.get("totalCount");

  return (
    <Button
      onClick={() => {
        if (partsTotalCount > EXPORT_LIMIT) {
          toastr.info(
            "Processing",
            "Please check your notifications for completion."
          );
          api.parts.export(params).then(() => SUCCESS);
        } else {
          window.open(
            `${API_BASE}/parts/export?${stringify(params)}`,
            "_blank"
          );
          toastr.success(
            "Processing Parts Report. Please wait for the PDF to generate."
          );
        }
      }}
    >
      Export
    </Button>
  );
};

ExportButton.propTypes = {
  searchProps: T.shape({
    paginator: T.instanceOf(Map)
  })
};

const meetsExportCriteria = renderIf(
  ({ searchProps, currentUser }) =>
    !searchProps.isLoading &&
    (searchProps.paginator.get("totalCount") <= 150 ||
      currentUser.get(ROLE) === ADMIN)
);

const DecoratedExportButton = meetsExportCriteria(ExportButton);

function PartsSearch({ currentUser, pushHistory }) {
  const [applications, setApplications] = useState([]);
  const [applicationSearch, setApplicationSearch] = useState("");
  const [applicationSearchOpen, setApplicationSearchOpen] = useState(false);
  const [allSearchedApplications, setAllSearchedApplications] = useState([]);

  const args = useDebounce(500, applicationSearch);
  const itHasPermission = hasPermission(
    currentUser.toJS(),
    "can_view_active_parts"
  );
  const showActiveParts = itHasPermission;

  let filterOptions = [...defaultFilters];

  useEffect(
    () => {
      if (applicationSearch.length > 1) {
        Promise.all([
          api.engines
            .search({ limit: 100, filter: applicationSearch })
            .then(({ data: { results } }) => results.map(e => e.model_number)),
          api.aircrafts
            .search({ limit: 100, filter: applicationSearch })
            .then(({ data: { results } }) => results.map(e => e.model_number))
        ]).then(models => {
          setAllSearchedApplications(
            uniq(allSearchedApplications.concat(flatten(models)))
          );

          setApplications(flatten(models));
        });
      }
    },
    [args]
  );

  const applicationFilters = applications.map(k => ({
    label: k,
    filters: { [`application_${k}`.replace(/-|,|\(|\)| |]/g, "_")]: true }
  }));

  const applicationTags = allSearchedApplications.map(k => ({
    label: k,
    key: `application_${k}`.replace(/-|,|\(|\)| |]/g, "_")
  }));

  const tags = filterTags.concat(applicationTags);

  return (
    <Search search={partsPagination} syncWithUrl>
      {searchProps => {
        const filters = searchProps.paginator.toJS().filters;
        const filter = filters.filter;
        const meetsConditions =
          (!!filter && filter.length > 3) ||
          Object.keys(filters).some(
            f => f.includes("application") && filters[f]
          );

        if (showActiveParts || meetsConditions) {
          filterOptions = [
            {
              label: "Active Parts",
              filters: {
                active: true
              }
            },
            {
              label: "Sellable Qty",
              filters: {
                sellable_qty: true
              }
            },
            ...defaultFilters
          ];
        } else {
          filterOptions = [...defaultFilters];
        }
        return (
          <div>
            <div className="form-group form-group-searchbar has-feedback has-feedback-left">
              <i className="text-muted fa fa-search form-control-feedback form-control-feedback-left" />
              <div className="input-group">
                <SearchFilter
                  {...searchProps}
                  placeholder="Search by typing a Part Number, Part Description, MFG or Cage Code..."
                  submitOnSingleResult={r => pushHistory(`/parts/${r.uuid}`)}
                  filters={tags}
                />
                <DecoratedExportButton
                  searchProps={searchProps}
                  currentUser={currentUser}
                />
                <SearchableFilterSelect
                  {...searchProps}
                  filters={applicationFilters.filter(
                    f =>
                      !Object.keys(filters).includes(Object.keys(f.filters)[0])
                  )}
                  right
                  searchValue={applicationSearch}
                  title="Appl."
                  visible={applicationSearchOpen}
                  toggle={() => {
                    setApplicationSearchOpen(!applicationSearchOpen);
                  }}
                  searchFilters={e =>
                    setApplicationSearch(get(e, "target.value", ""))}
                />
                <FilterSelect
                  {...searchProps}
                  filters={filterOptions}
                  right
                  display
                />
                <Authorized permissions={[CAN_ADD_UPDATE_PARTS]}>
                  <NewPart />
                </Authorized>
              </div>
            </div>
            <TitleWithFilters {...searchProps} title="Parts" filters={tags} />
            <Table {...searchProps} />
            <Pagination {...searchProps} />
          </div>
        );
      }}
    </Search>
  );
}

PartsSearch.propTypes = {
  currentUser: T.instanceOf(Map),
  pushHistory: T.func.isRequired
};

export default connect(
  state => ({
    currentUser: state.session.get(USER)
  }),
  { pushHistory: push }
)(PartsSearch);



// WEBPACK FOOTER //
// ./app/modules/parts/components/Search/Search.jsx